import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../AuthContext';
import { API_BASE_URL } from '../config';
import MachineMap from './MachineMap';
import MapFilterBar from './MapFilterBar';
import MappingIssues from './MappingIssues';
import PlanningPanel from './PlanningPanel';
import { splitMappable } from './mapData';
import { DEFAULT_FILTERS, applyFilters, countPerProvince, hasActiveFilters } from './mapFilters';
import { DEFAULT_MARKER_STYLE, isMarkerStyle } from './markerStyle';
import { resolvePlanning } from './planning';
import { usePlanning } from './usePlanning';
import './Accounts.css';
import './AccountMaps.css';

// DJB-239: Accounts › Maps sub-page, slotted between Overview and Manager.
// Same page treatment as Manager (DJB-171) and Settings (DJB-208): the
// 1800px .accounts-page container directly in the page content.
//
// DJB-240: the page loads the whole in-fleet from GET /machines/map-data
// (DJB-238: {machineId, name, city, province, plan, cash, mobile,
// daysSinceCashout, lastSeen, latitude, longitude} per machine) and hands the
// machines with coordinates to the map. Machines without coordinates are
// skipped and only counted in the note above the map. The map itself renders
// straight away — pins drop in once the data arrives.
//
// DJB-242: the search bar + province / cash / days toggles (MapFilterBar.js)
// narrow the pinned machines live; the rules live in mapFilters.js and all
// combine (AND). The cluster layer re-reads the list, so clusters and their
// counts follow the active filters.
//
// DJB-243: the Standard / Detail marker-style toggle (markerStyle.js) lives
// next to the search bar; the choice is page state handed to the map.
// DJB-245: the "Online" toggle is one more filter in the same state.
// DJB-257: map-data rows also carry openDays + todayStatus; the pin card
// shows the open-days dots and the "Open" toggle (open today) joins Online.
// DJB-247: the machines without coordinates (offMap) feed the "Resolve
// mapping issues" table under the map; saving an address there PUTs the
// machine (backend re-geocodes) and re-fetches the map data, so a fixed
// machine moves from the table onto the map without a reload.
//
// DJB-248 / DJB-249: the Planning panel (PlanningPanel.js) sits next to the
// map (under it on mobile). The planning itself is an ordered list of machine
// ids in usePlanning.js (localStorage, 10 h TTL); the entries are resolved
// against the loaded map data here, so the panel never shows anything the
// map does not know. "Add to planning" lives in the pin card (pinCard.js),
// "Show in Maps" hands the machine to the map as a focus request.
export const MAP_DATA_PATH = '/machines/map-data';

const AccountMaps = () => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const [machines, setMachines] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [markerStyle, setMarkerStyle] = useState(DEFAULT_MARKER_STYLE);
  // bumped to re-fetch the map data (after an address fix in MappingIssues)
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  const changeMarkerStyle = (style) => {
    if (isMarkerStyle(style)) setMarkerStyle(style);
  };

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    let cancelled = false;
    // `machines` starts out null (loading); a later re-fetch keeps the
    // current pins on screen until the new data is in
    setError(null);
    fetch(`${API_BASE_URL}${MAP_DATA_PATH}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch map data (${response.status})`);
        }
        return response.json();
      })
      .then((body) => {
        if (!cancelled) {
          setMachines(Array.isArray(body) ? body : []);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          console.error('Error fetching machine map data:', fetchError);
          setError(fetchError);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, reloadCount]);

  const { onMap, offMap } = useMemo(() => splitMappable(machines), [machines]);
  const visible = useMemo(() => applyFilters(onMap, filters), [onMap, filters]);
  const provinceCounts = useMemo(() => countPerProvince(onMap, filters), [onMap, filters]);
  const filtering = hasActiveFilters(filters);

  // DJB-248/249: the planning (ids + order) and its entries as map-data rows
  const planning = usePlanning();
  const { ids: plannedIdList, add: addPlanned } = planning;
  const plannedEntries = useMemo(() => resolvePlanning(plannedIdList, onMap), [plannedIdList, onMap]);
  const addToPlanning = useCallback(
    (machine) => addPlanned(machine && machine.machineId),
    [addPlanned]
  );

  // "Show in Maps": a fresh object per click so the same entry can be
  // focused twice in a row; on mobile the map is above the panel, so bring
  // it back into view as well
  const mapColumnRef = useRef(null);
  const [focusMachine, setFocusMachine] = useState(null);
  const showInMaps = useCallback((machine) => {
    if (!machine) return;
    setFocusMachine({ machine, nonce: Date.now() + Math.random() });
    const column = mapColumnRef.current;
    if (column && typeof column.scrollIntoView === 'function') {
      column.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  let status;
  if (error) {
    status = (
      <span className="accounts-maps-status is-error" role="alert">
        Kaartdata kon niet geladen worden.
      </span>
    );
  } else if (machines === null) {
    status = <span className="accounts-maps-status">Machines laden…</span>;
  } else {
    status = (
      <span className="accounts-maps-status">
        {filtering ? `${visible.length} van ${onMap.length}` : onMap.length}{' '}
        {onMap.length === 1 ? 'machine' : 'machines'} op de kaart
        {offMap.length > 0 && (
          <span className="accounts-maps-off-map">
            {' · '}
            {offMap.length} niet op kaart (geen coördinaten)
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="accounts-page accounts-maps-page">
      <div className="accounts-maps-toolbar">
        <MapFilterBar
          machines={onMap}
          filters={filters}
          provinceCounts={provinceCounts}
          onChange={setFilters}
          markerStyle={markerStyle}
          onMarkerStyleChange={changeMarkerStyle}
        />
        {status}
      </div>
      <div className="accounts-maps-body">
        <div className="accounts-maps-map-column" ref={mapColumnRef}>
          <MachineMap
            machines={visible}
            markerStyle={markerStyle}
            plannedIds={planning.plannedIds}
            onAddToPlanning={addToPlanning}
            focusMachine={focusMachine}
          />
        </div>
        <PlanningPanel
          entries={plannedEntries}
          onShowInMaps={showInMaps}
          onRemove={planning.remove}
          onMove={planning.move}
          onMoveTo={planning.moveTo}
          onClear={planning.clear}
        />
      </div>
      {machines !== null && !error && <MappingIssues machines={offMap} onSaved={reload} />}
    </div>
  );
};

export default AccountMaps;
